import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Button, Card, Container, Divider, Stack, Typography,
} from '@mui/material';
import {
  AccountTreeOutlined as EngineIcon,
  ChecklistRtlOutlined as TaskIcon,
  FolderCopyOutlined as VaultIcon,
  BuildOutlined as MaintenanceIcon,
  ForumOutlined as ChatIcon,
  InsightsOutlined as VisibilityIcon,
  LockOutlined as LockIcon,
  VisibilityOffOutlined as VisibilityOffIcon,
  RestoreOutlined as RestoreIcon,
  ArrowForwardRounded as ArrowIcon,
  CheckRounded as CheckIcon,
} from '@mui/icons-material';
import { PublicHeader, PublicFooter, useSignInLink } from '../components/layout/PublicLayout';
import { Reveal } from '../components/marketing/motion';
import OperationDemo from '../components/marketing/OperationDemo';
import HowItWorks from '../components/marketing/HowItWorks';
import { SUPPORT_EMAIL } from './legal/legalContent';
import StoreButtons from '../components/marketing/StoreButtons';
import { status as statusTokens } from '../theme';

// The public page.
//
// Three earlier versions failed in three different ways: a generic SaaS
// template with invented uptime figures; an honest page that described only
// the document vault; and a full-product page that was accurate but which
// somebody who had never heard of us could read end to end and still not
// know what the software did. Too many words, and a picture of a compliance
// meter that taught nothing.
//
// So this one shows first and explains second. The hero is a morning
// running itself — a routine starting on a clock, a step waiting on a named
// person, an escalation when nobody answers — and the section under it
// draws the mechanism in three steps while it scrolls. Every card body is
// one line. If the picture needs a paragraph to work, the picture is wrong.
//
// The old rules still hold: no claim that cannot be traced to something
// that runs, and nothing here that V1_LAUNCH_PLAN.md cut from the release.

const PILLARS = [
  { icon: <EngineIcon />, title: 'Guided routines', body: 'Dispatch, onboarding, incidents, repairs — step by step.' },
  { icon: <TaskIcon />, title: 'Tasks that chase themselves', body: 'Due dates, proof, and overdue without anyone noticing.' },
  { icon: <VaultIcon />, title: 'Files that know their expiry', body: 'Renewals open before the date, not after it.' },
  { icon: <MaintenanceIcon />, title: 'Fleet and repairs', body: 'Vans, paperwork and downtime in one place.' },
  { icon: <ChatIcon />, title: 'Chat where the work is', body: 'Ask about a step beside the step.' },
  { icon: <VisibilityIcon />, title: 'Every area, one screen', body: 'What ran, what slipped, who owes what.' },
];

// The audit-trail line that used to sit here — "you can answer who saw
// this?" — was true but abstract, and buying decisions are not made on it.
// What an owner actually asks is who can see what, and whether anything can
// go missing.
const PRINCIPLES = [
  { icon: <VisibilityOffIcon />, title: 'Only the people who should', body: 'A driver sees their own file, never the depot’s. Set per role, per document type.' },
  { icon: <LockIcon />, title: 'No link outlives your access', body: 'Documents are never handed out as URLs. Every read is checked as it happens.' },
  { icon: <RestoreIcon />, title: 'Nothing quietly disappears', body: 'Deleted files come back for 30 days, and nothing tied to an open claim can be purged.' },
];

const ROLES = [
  { role: 'Owners', body: 'The whole operation, every area, on one screen.' },
  { role: 'Managers', body: 'Their area — what is running and what slipped.' },
  { role: 'Drivers', body: 'Today’s steps on the phone, and a guided way to report a problem.' },
  { role: 'Maintenance', body: 'Repairs to approve or carry out, with the evidence attached.' },
];

const Landing: React.FC = () => {
  const { hash } = useLocation();
  const signIn = useSignInLink();

  // "What it does" is a link in the shared header, which also renders on
  // /privacy and /terms — so it has to arrive here as /#product and be
  // scrolled to on mount, not just work as an in-page anchor.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  const contactProps = { href: `mailto:${SUPPORT_EMAIL}?subject=TopoSDS%20enquiry` };

  return (
    <Box sx={{ bgcolor: 'background.default', overflowX: 'clip' }}>
      <PublicHeader />

      {/* ----------------------------------------------------------- hero */}
      <Container maxWidth="lg" sx={{ pt: { xs: 7, md: 11 }, pb: { xs: 7, md: 11 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 5, md: 7 },
            alignItems: 'center',
          }}
        >
          <Reveal>
            <Typography variant="overline" sx={{ color: 'primary.main', display: 'block', mb: 1.5 }}>
              For FedEx contractors and delivery fleets
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.25rem', md: '3.25rem' }, lineHeight: 1.08, mb: 2.5 }}
            >
              Your operation, running itself.
            </Typography>
            <Typography sx={{ fontSize: 19, lineHeight: 1.6, color: 'text.secondary', maxWidth: 520, mb: 4 }}>
              TopoSDS tells every person what to do and when, chases what nobody did, and keeps the proof.
              You watch the whole thing from one screen.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <Button component={RouterLink} to={signIn.to} variant="contained" size="large" sx={{ py: 1.25, px: 3 }}>
                {signIn.label}
              </Button>
              <Button
                {...contactProps}
                variant="outlined"
                size="large"
                endIcon={<ArrowIcon sx={{ fontSize: 16 }} />}
                sx={{ py: 1.25, px: 3 }}
              >
                Book a walkthrough
              </Button>
            </Stack>
          </Reveal>

          <Reveal delay={120}>
            <OperationDemo />
          </Reveal>
        </Box>
      </Container>

      {/* --------------------------------------------------- how it works */}
      <HowItWorks />

      {/* --------------------------------------------------------- product */}
      <Box id="product" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
          <Reveal>
            <Typography variant="h2" sx={{ mb: 5 }}>Everything it runs</Typography>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            {PILLARS.map((item, index) => (
              <Reveal key={item.title} delay={index * 60}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: 2.5, mb: 2,
                      display: 'grid', placeItems: 'center',
                      bgcolor: 'primary.light', color: 'primary.dark',
                      '& svg': { fontSize: 21 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 0.75 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {item.body}
                  </Typography>
                </Card>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ----------------------------------------------------------- roles */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
            gap: { xs: 3, md: 7 },
            alignItems: 'start',
          }}
        >
          <Reveal>
            <Typography variant="h2">Everyone is told what to do next</Typography>
          </Reveal>

          <Box>
            <Stack divider={<Divider />}>
              {ROLES.map((item, index) => (
                <Reveal key={item.role} delay={index * 60}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={{ xs: 0.5, sm: 3 }}
                    sx={{ py: 2.25 }}
                  >
                    <Typography variant="subtitle1" sx={{ minWidth: 140 }}>{item.role}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {item.body}
                    </Typography>
                  </Stack>
                </Reveal>
              ))}
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* ------------------------------------------------------------- app */}
      <Box id="download" sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 4, md: 7 },
              alignItems: 'center',
            }}
          >
            <Reveal>
              <Typography variant="h2" sx={{ mb: 1.5 }}>Your crew works from their phone</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 17, lineHeight: 1.65, mb: 3.5, maxWidth: 460 }}>
                Drivers and maintenance get their steps, proof and chat on iOS and Android. Owners and
                managers run the desk half in a browser.
              </Typography>

              <StoreButtons />
            </Reveal>

            <Reveal delay={120}>
              <Card sx={{ p: { xs: 2.5, md: 3 }, maxWidth: 420, mx: { xs: 0, md: 'auto' }, width: '100%' }}>
                <Typography variant="overline" color="text.disabled" display="block" sx={{ mb: 1.5 }}>
                  Your next step
                </Typography>
                <Typography variant="h5" sx={{ mb: 0.5 }}>Start-of-shift check</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Van 214 · due in 25 minutes
                </Typography>

                <Stack gap={1.5}>
                  {[['Walk-around and tyres', true], ['Photograph any damage', true], ['Load matches manifest', false]].map(
                    ([label, done]) => (
                      <Stack key={String(label)} direction="row" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                            display: 'grid', placeItems: 'center',
                            border: '2px solid',
                            borderColor: done ? statusTokens.ok : statusTokens.warn,
                            bgcolor: done ? statusTokens.ok : 'transparent',
                            color: '#fff',
                          }}
                        >
                          {done ? <CheckIcon sx={{ fontSize: 12 }} /> : null}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: done ? 'text.disabled' : 'text.primary',
                            fontWeight: done ? 400 : 600,
                            textDecoration: done ? 'line-through' : 'none',
                          }}
                        >
                          {label}
                        </Typography>
                      </Stack>
                    ),
                  )}
                </Stack>
              </Card>
            </Reveal>
          </Box>
        </Container>
      </Box>

      {/* ------------------------------------------------------ principles */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Reveal>
          <Typography variant="h2" sx={{ mb: 5 }}>How it treats your records</Typography>
        </Reveal>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {PRINCIPLES.map((item, index) => (
            <Reveal key={item.title} delay={index * 60}>
              <Box sx={{ color: 'primary.main', mb: 1.5, '& svg': { fontSize: 24 } }}>{item.icon}</Box>
              <Typography variant="h5" sx={{ mb: 1 }}>{item.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {item.body}
              </Typography>
            </Reveal>
          ))}
        </Box>
      </Container>

      {/* --------------------------------------------------------- closer */}
      <Container maxWidth="lg" sx={{ pb: { xs: 7, md: 10 } }}>
        <Reveal>
          <Card sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ mb: 1.5 }}>See your own morning in it</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 17, maxWidth: 500, mx: 'auto', mb: 3.5 }}>
              Tell us how your day starts today. We will show you it running.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} justifyContent="center">
              <Button {...contactProps} variant="contained" size="large" sx={{ py: 1.25, px: 3.5 }}>
                Book a walkthrough
              </Button>
              <Button component={RouterLink} to="/support" variant="outlined" size="large" sx={{ py: 1.25, px: 3.5 }}>
                Already a customer?
              </Button>
            </Stack>
          </Card>
        </Reveal>
      </Container>

      <PublicFooter />
    </Box>
  );
};

export default Landing;
